import { Skeleton, Stack } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import {
  FederalStates,
  type FederalStateKey,
  type FormularABFormData,
} from '../../types/reports';
import { FormCard } from '../ui/FormCard';
import { FormSubsection } from '../ui/FormSubsection';
import { SummaryBar } from '../ui/SummaryBar';
import { ValidatedNumberInput } from '../form/validatedFields';
import { PreviousValueForField } from '../form/PreviousValueLine';
import { DECIMAL_INPUT_PROPS, FIELD_LIST_MAX_WIDTH } from '../form/numberFieldPresets';
import { orderedFederalStateKeys } from './federalStateOrder';

/** Shape returned by POST /api/reports/calculate for type "formular_ab" (FormularABCalculator, 18-01). */
export interface FormularABCalculations {
  inlandsVersand: string | null;
}

interface FormularABFormProps {
  form: UseFormReturnType<FormularABFormData>;
  runValidation: () => void;
  /** Server-Vorschau der Summe (D-08) -- strikt read-only, wird nie in die form zurueckgeschrieben. */
  calc: FormularABCalculations | null;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Praesentations-Shell (D-01): keine eigene form-Instanz, keine Client-Summe mehr. Die
 * Bundeslandfelder haengen an der geteilten form-Instanz aus ReportEditPage, die Summe kommt
 * als Server-Vorschau aus useReportCalc -- exakt das waterConsumption-Muster aus WasserSection.
 */
export function FormularABForm({ form, runValidation, calc, disabled, loading }: FormularABFormProps) {
  const renderStateField = (stateKey: FederalStateKey) =>
    loading ? (
      <Skeleton height={60} radius="sm" key={stateKey} />
    ) : (
      <ValidatedNumberInput
        key={stateKey}
        form={form}
        field={stateKey}
        kind="decimal"
        align="right"
        label={FederalStates[stateKey]}
        disabled={disabled}
        suffix=" t"
        maw={FIELD_LIST_MAX_WIDTH}
        {...DECIMAL_INPUT_PROPS}
      />
    );

  return (
    <div onBlur={runValidation}>
      <Stack gap="md">
        <FormCard>
          <Stack gap="md">
            <FormSubsection
              title="Inlandsversand nach Bundesländern"
              description="Geben Sie die Versandmengen für jedes Bundesland in Tonnen ein."
            >
              {/*
                Ein Bundesland je Zeile (D-29, Sketch 003 D: „Liste der Bundeslaender
                untereinander"). Bis Plan 23-09 stand hier ein `Grid` mit
                `span={{ base: 6, sm: 4, md: 3 }}`, also ein vierspaltiges Raster — das
                widersprach dem must_have des eigenen Plans.
              */}
              <Stack gap="sm">{orderedFederalStateKeys.map(renderStateField)}</Stack>
            </FormSubsection>

            {/* Der Summenbalken steht im Sketch INNERHALB des weissen Rahmens, nicht darunter. */}
            <SummaryBar
              label="Inlandsversand Gesamt"
              value={calc?.inlandsVersand ?? null}
              variant="highlight"
              loading={loading}
            />
            <PreviousValueForField field="inlandsVersand" />
          </Stack>
        </FormCard>
      </Stack>
    </div>
  );
}
